import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import type { Tables } from '@/integrations/supabase/types';
import { sendNotification } from '@/lib/notifications';

export type MatchJoinRequest = Tables<'match_join_requests'> & {
  profile?: Tables<'profiles'>;
};

// Server-side deduplication: unique index `match_join_requests_match_id_profile_id_key`
// on (match_id, profile_id) enforces uniqueness across all devices/sessions.
// Duplicate inserts return Postgres error code 23505, handled in onError below.



export function useMatchJoinRequests(matchId: string, statusFilter: 'pending' | 'all' = 'pending') {
  return useQuery({
    queryKey: ['match-join-requests', matchId, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('match_join_requests')
        .select(`
          *,
          profile:profiles!match_join_requests_profile_id_fkey(*)
        `)
        .eq('match_id', matchId);

      if (statusFilter === 'pending') {
        query = query.eq('status', 'pending');
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data as MatchJoinRequest[];
    },
    enabled: !!matchId,
  });
}

export function useMyMatchJoinRequest(matchId: string) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['my-match-join-request', matchId, profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;

      const { data, error } = await supabase
        .from('match_join_requests')
        .select('*')
        .eq('match_id', matchId)
        .eq('profile_id', profile.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!matchId && !!profile?.id,
  });
}

export function useRequestJoinMatch() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      matchId,
      team,
      message,
    }: {
      matchId: string;
      team?: number | null;
      message?: string;
    }) => {
      if (!profile?.id) throw new Error('Chưa đăng nhập');

      // No client-side lock: dedup is enforced server-side by the unique index
      // on (match_id, profile_id). Concurrent inserts from any device fail with 23505.

      // Check if player is banned
      const { data: isBanned, error: banError } = await supabase.rpc('is_player_banned', {
        _profile_id: profile.id,
      });
      if (banError) throw banError;
      if (isBanned) {
        throw new Error('Bạn đang bị cấm thi đấu. Vui lòng đợi hết thời hạn hoặc gửi đơn kháng cáo.');
      }


        // Check if the match is ranked
        const { data: matchData, error: matchError } = await supabase
          .from('matches')
          .select('is_ranked, created_by, sport, recruiting_players')
          .eq('id', matchId)
          .single();

        if (matchError) throw matchError;

        // If ranked, check skill level and ranked sport compatibility.
        // IMPORTANT: always re-fetch the requester's profile from the DB instead
        // of trusting `profile` from AuthContext, which may be stale right after
        // the user (or an admin) just updated skill_level / ranked_sport.
        if (matchData.is_ranked) {
          // NOTE: RLS trên `profiles` chỉ cho đọc của chính user.
          // Dùng view `profiles_public` để đọc dữ liệu công khai của user khác
          // (skill_level, ranked_sport, name, avatar) và luôn dùng `.maybeSingle()`.
          const [creatorRes, requesterRes] = await Promise.all([
            supabase
              .from('profiles_public')
              .select('skill_level, ranked_sport')
              .eq('id', matchData.created_by)
              .maybeSingle(),
            supabase
              .from('profiles')
              .select('skill_level, ranked_sport')
              .eq('id', profile.id)
              .maybeSingle(),
          ]);

          if (creatorRes.error) throw creatorRes.error;
          if (requesterRes.error) throw requesterRes.error;

          const creatorSkillLevel = creatorRes.data?.skill_level;
          const mySkillLevel = requesterRes.data?.skill_level;
          const myRankedSport = requesterRes.data?.ranked_sport;

          // Check skill level (only enforce when both sides have one)
          if (mySkillLevel && creatorSkillLevel && mySkillLevel !== creatorSkillLevel) {
            throw new Error('Trận đấu rank chỉ cho phép người chơi cùng trình độ');
          }

          // Check ranked sport
          if (myRankedSport && myRankedSport !== matchData.sport) {
            throw new Error(`Bạn chỉ có thể tham gia trận rank với môn ${myRankedSport}`);
          }

          // Check daily ranked match limit AND same-opponent limit atomically via RPC
          const { data: limitCheck, error: limitError } = await supabase.rpc('check_daily_ranked_limit', {
            player_id: profile.id,
            opponent_id: matchData.created_by,
          });

          if (limitError) throw limitError;

          if (limitCheck && limitCheck.length > 0 && !limitCheck[0].can_play) {
            throw new Error(limitCheck[0].reason || 'Đã đạt giới hạn trận rank hôm nay');
          }
        }

        // Check if match is accepting join requests
        if (!matchData.recruiting_players) {
          throw new Error('Trận đấu này không tuyển thêm người chơi');
        }

        // Check schedule conflict
        const { data: matchDetail, error: matchDetailError } = await supabase
          .from('matches')
          .select('date, time')
          .eq('id', matchId)
          .single();

        if (matchDetailError) throw matchDetailError;

        const { data: hasConflict, error: conflictError } = await supabase.rpc('check_schedule_conflict', {
          _profile_id: profile.id,
          _match_date: matchDetail.date,
          _match_time: matchDetail.time,
          _exclude_match_id: matchId,
        });
        if (conflictError) throw conflictError;
        if (hasConflict) {
          throw new Error('Bạn đã có lịch tham gia một trận đấu khác trong khoảng thời gian này');
        }

        // Check if already a participant in this match (double booking prevention)
        const { data: existingParticipant } = await supabase
          .from('match_participants')
          .select('id')
          .eq('match_id', matchId)
          .eq('profile_id', profile.id)
          .maybeSingle();

        if (existingParticipant) {
          throw new Error('Bạn đã tham gia trận đấu này rồi. Vui lòng rời trận trước khi đổi đội.');
        }

        // Check if user is currently a spectator - warn about upgrade
        const { data: existingSpectator } = await supabase
          .from('match_spectators')
          .select('id, status, payment_status')
          .eq('match_id', matchId)
          .eq('profile_id', profile.id)
          .in('status', ['pending', 'approved'])
          .maybeSingle();

        if (existingSpectator) {
          // Allow the request but flag it - spectator record will be auto-cancelled
          // by safe_add_match_participant when they are added as a player.
          // The user must pay the full entry_fee regardless of spectator_fee paid.
          console.log('User is upgrading from spectator to player for match:', matchId);
        }

        // Atomic check: match has space (prevents race condition)
        const rpcParams: Record<string, unknown> = { _match_id: matchId };
        if (team != null) rpcParams._team = team;
        const { data: hasSpace, error: spaceError } = await supabase.rpc('check_match_has_space', rpcParams as any);
        if (spaceError) throw spaceError;
        if (!hasSpace) {
          throw new Error(team ? 'Đội đã đầy thành viên. Vui lòng chọn đội khác.' : 'Trận đấu đã đủ số lượng người chơi');
        }

        // Check if already has pending request
        const { data: existingRequest } = await supabase
          .from('match_join_requests')
          .select('id, status')
          .eq('match_id', matchId)
          .eq('profile_id', profile.id)
          .maybeSingle();

        if (existingRequest) {
          if (existingRequest.status === 'pending') {
            throw new Error('Bạn đã gửi yêu cầu tham gia trận này');
          }
          if (existingRequest.status === 'approved') {
            throw new Error('Yêu cầu của bạn đã được duyệt');
          }
        }

        const { data, error } = await supabase
          .from('match_join_requests')
          .insert({
            match_id: matchId,
            profile_id: profile.id,
            team,
            message,
          })
          .select()
          .single();

      if (error) throw error;
      return data;

    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match-join-requests', variables.matchId] });
      queryClient.invalidateQueries({ queryKey: ['my-match-join-request', variables.matchId] });
      toast.success('Đã gửi yêu cầu tham gia');
    },
    onError: (error: any) => {
      // Graceful handling for duplicate insert caused by DB unique constraint
      if (
        error?.code === '23505' ||
        error?.message?.includes('match_join_requests_match_id_profile_id_key') ||
        error?.message?.includes('duplicate key')
      ) {
        toast.error('Bạn đã gửi yêu cầu tham gia trận này rồi');
        return;
      }
      toast.error('Lỗi gửi yêu cầu', {
        description: error?.message || 'Có lỗi xảy ra',
      });
    },
  });
}

export function useRespondToJoinRequest() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      requestId,
      matchId,
      profileId,
      approved,
      team,
      paymentDeadline,
    }: {
      requestId: string;
      matchId: string;
      profileId: string;
      approved: boolean;
      team?: number | null;
      paymentDeadline?: string;
    }) => {
      if (!profile?.id) throw new Error('Chưa đăng nhập');

      // Check if player being approved is banned
      if (approved) {
        const { data: isBanned, error: banError } = await supabase.rpc('is_player_banned', {
          _profile_id: profileId,
        });
        if (banError) throw banError;
        if (isBanned) {
          throw new Error('Không thể duyệt: Người chơi này đang bị cấm thi đấu');
        }
      }

      // Get match details
      const { data: matchData, error: matchError } = await supabase
        .from('matches')
        .select('*, match_participants(*)')
        .eq('id', matchId)
        .single();

      if (matchError) throw matchError;

      // Calculate match start time
      const matchStartTime = new Date(`${matchData.date}T${matchData.time}`);
      const now = new Date();
      
      // Check if match has already started
      if (matchStartTime <= now) {
        throw new Error('Không thể duyệt yêu cầu vì trận đấu đã bắt đầu');
      }

      // Determine fee
      const recruitingFee = matchData.recruiting_fee || 0;
      const matchFee = matchData.fee || 0;
      const feeAmount = recruitingFee > 0 ? recruitingFee : matchFee;

      // Update request status
      const updateData: Record<string, unknown> = {
        status: approved ? 'approved' : 'rejected',
        reviewed_at: new Date().toISOString(),
        reviewed_by: profile.id,
      };

      // Add payment info if approved and match has fee
      if (approved && feeAmount > 0) {
        // Enforce deadline to be at least 1 hour before match start
        const maxDeadline = new Date(matchStartTime.getTime() - 60 * 60 * 1000); // 1 hour before
        let finalDeadline: Date;
        
        if (paymentDeadline) {
          const requestedDeadline = new Date(paymentDeadline);
          finalDeadline = requestedDeadline > maxDeadline ? maxDeadline : requestedDeadline;
        } else {
          // Default: always 1 hour before match start
          finalDeadline = maxDeadline;
        }
        
        // Final check: deadline must be in the future
        if (finalDeadline <= now) {
          throw new Error('Thời hạn thanh toán phải trước khi trận đấu diễn ra. Trận đấu sẽ bắt đầu quá sớm.');
        }
        
        updateData.payment_deadline = finalDeadline.toISOString();
        updateData.payment_status = 'pending';
      }

      // BUG #7 FIX: Check capacity BEFORE updating status to avoid race window
      if (approved) {
        // Atomic capacity check before approval (including per-team check)
        const approveRpcParams: Record<string, unknown> = { _match_id: matchId };
        if (team != null) approveRpcParams._team = team;
        const { data: hasSpace, error: spaceError } = await supabase.rpc('check_match_has_space', approveRpcParams as any);
        if (spaceError) throw spaceError;
        if (!hasSpace) {
          if (team) {
            throw new Error('Đội đã đầy thành viên, không thể duyệt thêm');
          }
          const slots = (matchData as any)?.recruiting_slots;
          if ((matchData as any)?.recruiting_players && slots > 0) {
            throw new Error(`Đã đủ số người tuyển (${slots}/${slots}), không thể duyệt thêm`);
          }
          throw new Error('Trận đấu đã đủ số lượng người chơi, không thể duyệt thêm');
        }
      }

      const { error: updateError } = await supabase
        .from('match_join_requests')
        .update(updateData)
        .eq('id', requestId);

      if (updateError) throw updateError;

      // If approved (capacity already verified above)
      if (approved) {

        const currentParticipants = matchData.match_participants?.length || 0;
        const isRecruitingRanked = matchData.is_ranked && matchData.recruiting_players;

        // For ranked 1v1 matches with recruiting: create separate match
        if (isRecruitingRanked && matchData.match_type === '1v1') {
          // BUG #13 FIX: Validate referee_id exists for ranked matches
          if (!matchData.referee_id) {
            throw new Error('Trận đấu xếp hạng cần có trọng tài. Vui lòng chỉ định trọng tài trước khi duyệt.');
          }

          // Create new 1v1 ranked match between creator and approved player
          const { data: newMatch, error: newMatchError } = await supabase
            .from('matches')
            .insert({
              sport: matchData.sport,
              date: matchData.date,
              time: matchData.time,
              location: matchData.location,
              description: matchData.description,
              match_type: '1v1',
              is_ranked: true,
              is_open: false,
              fee: matchFee,
              created_by: matchData.created_by,
              referee_id: matchData.referee_id,
              status: 'upcoming',
            })
            .select()
            .single();

          if (newMatchError) throw newMatchError;

          if (feeAmount > 0) {
            // Paid match: only add creator (team 1), approved player added after payment via escrow-webhook
            const { error: creatorError } = await supabase
              .from('match_participants')
              .insert({ match_id: newMatch.id, profile_id: matchData.created_by, team: 1 });
            if (creatorError) throw creatorError;

            // Update join request with new match reference and payment info
            const matchStart = new Date(`${matchData.date}T${matchData.time}`);
            const paymentDeadline = new Date(matchStart.getTime() - 60 * 60 * 1000);
            await supabase
              .from('match_join_requests')
              .update({
                match_id: newMatch.id,
                payment_status: 'pending',
                payment_deadline: paymentDeadline.toISOString(),
              })
              .eq('id', requestId);

            return { approved, newMatchId: newMatch.id, requiresPayment: true };
          }

          // Free ranked match: add both players immediately
          const { error: participantsError } = await supabase
            .from('match_participants')
            .insert([
              { match_id: newMatch.id, profile_id: matchData.created_by, team: 1 },
              { match_id: newMatch.id, profile_id: profileId, team: 2 },
            ]);

          if (participantsError) throw participantsError;

          return { approved, newMatchId: newMatch.id, requiresPayment: false };
        }

        // For regular matches: if no fee or already paid, add directly
        // If has fee and not paid, wait for payment
        if (feeAmount === 0) {
          // Free match - add participant atomically (prevents race condition)
          const { error: participantError } = await supabase.rpc('safe_add_match_participant', {
            _match_id: matchId,
            _profile_id: profileId,
            _team: team || null,
          });

          if (participantError) throw new Error(participantError.message);
        }
        // If has fee, participant will be added after payment confirmation
      }

      return { approved, requiresPayment: approved && feeAmount > 0 };
    },
    onSuccess: (result: { approved: boolean; newMatchId?: string; requiresPayment?: boolean }, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match-join-requests', variables.matchId] });
      queryClient.invalidateQueries({ queryKey: ['match', variables.matchId] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      
      if (result.approved) {
        if (result.newMatchId) {
          toast.success('Đã tạo trận đấu 1v1 tính điểm mới');
        } else if (result.requiresPayment) {
          toast.success('Đã duyệt yêu cầu', {
            description: 'Người chơi cần thanh toán trong thời hạn để được tham gia',
          });
        } else {
          toast.success('Đã chấp nhận yêu cầu');
        }
      } else {
        toast.success('Đã từ chối yêu cầu');
      }
    },
    onError: (error: Error) => {
      toast.error('Lỗi xử lý yêu cầu', {
        description: error.message,
      });
    },
  });
}

/**
 * Cancel a join request (pending status - no payment involved)
 */
export function useCancelJoinRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      requestId,
      matchId,
    }: {
      requestId: string;
      matchId: string;
    }) => {
      const { error } = await supabase
        .from('match_join_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match-join-requests', variables.matchId] });
      queryClient.invalidateQueries({ queryKey: ['my-match-join-request', variables.matchId] });
      toast.success('Đã hủy yêu cầu tham gia');
    },
    onError: (error: Error) => {
      toast.error('Lỗi hủy yêu cầu', {
        description: error.message,
      });
    },
  });
}

/**
 * Leave a match (approved participant) with escrow refund/penalty logic.
 * 
 * - Before deadline (2h before match): full refund
 * - After deadline: 50% penalty
 */
export function useLeaveMatch() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      matchId,
      penaltyPercent,
    }: {
      matchId: string;
      penaltyPercent: number; // 0 = full refund, 50 = 50% penalty
    }) => {
      if (!profile?.id) throw new Error('Chưa đăng nhập');

      // BUG #10 FIX: Block leaving match that is ongoing or completed
      const { data: matchStatus, error: matchStatusError } = await supabase
        .from('matches')
        .select('status')
        .eq('id', matchId)
        .single();

      if (matchStatusError) throw matchStatusError;

      if (matchStatus?.status === 'ongoing') {
        throw new Error('Không thể rời trận đấu đang diễn ra.');
      }
      if (matchStatus?.status === 'completed') {
        throw new Error('Không thể rời trận đấu đã hoàn thành.');
      }
      if (matchStatus?.status === 'cancelled') {
        throw new Error('Trận đấu đã bị hủy.');
      }

      // 1. Check if there's an escrow payment for this participant
      const { data: escrowPayments } = await supabase
        .from('escrow_payments')
        .select('*')
        .eq('activity_type', 'match')
        .eq('activity_id', matchId)
        .eq('payer_id', profile.id)
        .in('escrow_status', ['held', 'awaiting_payment']);

      // 2. Process escrow refund/penalty for each payment
      if (escrowPayments && escrowPayments.length > 0) {
        for (const payment of escrowPayments) {
          const { error } = await supabase.functions.invoke('cancel-transaction', {
            body: {
              escrow_payment_id: payment.id,
              reason: penaltyPercent > 0
                ? `Người chơi tự hủy tham gia sau deadline (phạt ${penaltyPercent}%)`
                : 'Người chơi tự hủy tham gia trước deadline',
              penalty_percent: penaltyPercent,
            }
          });
          if (error) {
            console.error('Error cancelling escrow payment:', error);
            throw new Error('Lỗi xử lý hoàn tiền. Vui lòng thử lại.');
          }
        }
      }

      // 3. Remove from match_participants
      const { error: removeError } = await supabase
        .from('match_participants')
        .delete()
        .eq('match_id', matchId)
        .eq('profile_id', profile.id);

      if (removeError) throw removeError;

      // 4. Update join request status to 'cancelled'
      await supabase
        .from('match_join_requests')
        .update({ status: 'cancelled' })
        .eq('match_id', matchId)
        .eq('profile_id', profile.id);

      // 5. Notify match creator
      const { data: match } = await supabase
        .from('matches')
        .select('created_by, sport, date')
        .eq('id', matchId)
        .single();

      if (match && match.created_by !== profile.id) {
        await sendNotification({
          kind: 'match_participant_left',
          target_profile_ids: [match.created_by],
          match_id: matchId,
          metadata: { penalty_percent: penaltyPercent },
        });
      }

      return { penaltyPercent };
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match', variables.matchId] });
      queryClient.invalidateQueries({ queryKey: ['match-join-requests', variables.matchId] });
      queryClient.invalidateQueries({ queryKey: ['my-match-join-request', variables.matchId] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      queryClient.invalidateQueries({ queryKey: ['activity-payment'] });
      
      if (result.penaltyPercent > 0) {
        toast.success('Đã hủy tham gia', {
          description: `Phí phạt ${result.penaltyPercent}% đã được áp dụng. Phần còn lại sẽ được hoàn trả.`,
        });
      } else {
        toast.success('Đã hủy tham gia', {
          description: 'Phí tham gia sẽ được hoàn trả 100%.',
        });
      }
    },
    onError: (error: Error) => {
      toast.error('Lỗi hủy tham gia', {
        description: error.message,
      });
    },
  });
}

// Hook to confirm payment and add participant
export function useConfirmJoinPayment() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      requestId,
      matchId,
      team,
    }: {
      requestId: string;
      matchId: string;
      team?: number | null;
    }) => {
      if (!profile?.id) throw new Error('Chưa đăng nhập');

      // Get the request
      const { data: request, error: requestError } = await supabase
        .from('match_join_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (requestError) throw requestError;

      if (request.status !== 'approved') {
        throw new Error('Yêu cầu chưa được duyệt');
      }

      // Update payment status
      const { error: updateError } = await supabase
        .from('match_join_requests')
        .update({
          payment_status: 'paid',
          payment_confirmed_at: new Date().toISOString(),
          payment_confirmed_by: profile.id,
        })
        .eq('id', requestId);

      if (updateError) throw updateError;

      // Add participant to match atomically (prevents race condition)
      const { error: participantError } = await supabase.rpc('safe_add_match_participant', {
        _match_id: matchId,
        _profile_id: request.profile_id,
        _team: team || request.team || null,
      });

      if (participantError) throw new Error(participantError.message);

      return { success: true };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match-join-requests', variables.matchId] });
      queryClient.invalidateQueries({ queryKey: ['match', variables.matchId] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      toast.success('Đã xác nhận thanh toán và thêm người chơi');
    },
    onError: (error: Error) => {
      toast.error('Lỗi xác nhận thanh toán', {
        description: error.message,
      });
    },
  });
}
